// ─────────────────────────────────────────────────────────────────────────────
// T-11 — mini-gráfica de tendencia para las tarjetas de KPI del Diario.
//
// Adriana en UAT (17-ago-2026), sobre el tab Diario: la maqueta trae una curvita bajo cada número y
// la pantalla solo muestra la cifra. La maqueta es `rep-panel-diario` de `fundamentales-admin.js`
// (`Diseño/sigma-tc-demo 31.zip`): SVG de 72×18, `polyline` de trazo 1.5 más un área traslúcida
// por debajo. Esas medidas se copian tal cual; lo que NO se copia es la escala, porque la maqueta
// trae puntos inventados.
// ─────────────────────────────────────────────────────────────────────────────

/** Medidas de la maqueta. El `viewBox` es fijo: el SVG escala con CSS, los puntos no. */
const ANCHO = 72;
const ALTO = 18;

/** Media pluma de margen arriba y abajo: sin esto el trazo de 1.5 se corta en el valor máximo. */
const MARGEN = 1;

export interface SparklineProps {
  /** Un valor por día del periodo, en orden cronológico. */
  readonly valores: readonly number[];
  /** Color del trazo. El área usa el mismo con transparencia. */
  readonly color: string;
  /** Descripción para lectores de pantalla — una `polyline` no se puede leer. */
  readonly aria: string;
}

/**
 * Convierte la serie en los puntos del `polyline`.
 *
 * 🔴 La escala arranca en CERO, no en el mínimo de la serie. Escalar mín-máx (lo que hace casi
 * cualquier librería de sparklines) convertiría un 2-3-2 en una cordillera: con conteos pequeños
 * amplifica ruido hasta que se lee como una tendencia. Además el área rellena hasta abajo, y un
 * relleno que no nace en cero miente sobre lo que representa.
 *
 * Exportada para poder probar la geometría sin montar el SVG.
 */
export function puntosDeSerie(valores: readonly number[]): string {
  const maximo = Math.max(...valores, 0);
  const util = ALTO - MARGEN * 2;
  const paso = valores.length > 1 ? ANCHO / (valores.length - 1) : 0;

  return valores
    .map((valor, i) => {
      // Serie toda en cero: línea plana abajo. No se centra a media altura — una raya en medio se
      // lee como «hubo actividad estable», y lo que hubo fue nada.
      const alturaRelativa = maximo === 0 ? 0 : valor / maximo;
      const y = ALTO - MARGEN - alturaRelativa * util;
      return `${Math.round(i * paso * 100) / 100},${Math.round(y * 100) / 100}`;
    })
    .join(' ');
}

export function Sparkline({ valores, color, aria }: SparklineProps) {
  // Con menos de dos días no hay tendencia que dibujar. Un punto suelto sería un pixel sin lectura
  // posible, y una serie vacía (periodo suprimido por RN-V05) no tiene que ocupar espacio.
  if (valores.length < 2) return null;

  const puntos = puntosDeSerie(valores);

  return (
    <svg
      className="rr-spark"
      viewBox={`0 0 ${ANCHO} ${ALTO}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={aria}
    >
      {/* El área va primero para que el trazo quede encima. Cierra por las dos esquinas de abajo. */}
      <polyline points={`${puntos} ${ANCHO},${ALTO} 0,${ALTO}`} fill={color} fillOpacity={0.1} stroke="none" />
      <polyline
        points={puntos}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}
